import React, { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
////// Mui
import { styled } from "@mui/material/styles";
import { useTheme, List } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import FlagIcon from "@mui/icons-material/Flag";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import GavelIcon from "@mui/icons-material/Gavel";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PaidIcon from "@mui/icons-material/Paid";
import QuizIcon from "@mui/icons-material/Quiz";
import EuroSymbolIcon from "@mui/icons-material/EuroSymbol";
////// Controls & Components
import { appURL } from "../Data/baseURL";
import InvIcon from "../Data/BasicIcons/InvIcon";
import QuoteIcon from "../Data/Icons/QuoteIcon";
import TooltipBasic from "../Controls/Inputs/TooltipBasic";
import { appChromePalette } from "../Public/PublicTheme";

const ListItemButtonStyled = styled(ListItemButton)(() => ({
  color: appChromePalette.muted,

  "&.MuiListItemButton-root": {
    paddingTop: 10,
    paddingBottom: 10,
  },
  "& svg": {
    color: "rgba(248,250,252,0.78)",
    transition: "0.2s",
    transform: "translateX(0) rotate(0)",
  },
  "&:hover, &:focus": {
    backgroundColor: appChromePalette.hover,
    "& svg:first-of-type": {
      transform: "translateX(-4px) rotate(-20deg)",
    },
    "& svg:last-of-type": {
      right: 0,
      opacity: 1,
    },
  },
  "&.Mui-selected": {
    color: appChromePalette.text,
    background: appChromePalette.selected,
    "& svg:first-of-type": {
      color: appChromePalette.accent,
    },
  },
  "&:after": {
    content: '""',
    position: "absolute",
    height: "80%",
    display: "block",
    left: 0,
    width: "1px",
    backgroundColor: "transparent",
  },
  "&.Mui-selected:after": {
    width: "2px",
    backgroundColor: appChromePalette.accent,
    boxShadow: `0 0 14px ${appChromePalette.accent}`,
  },
}));

const ListItemTextStyled = styled(ListItemText)(() => ({
  ".MuiListItemText-primary": { fontWeight: "500" },
}));

const ListItemIconStyled = styled(ListItemIcon)(() => ({
  color: "inherit",
}));

const HeaderDrawerList = (props) => {
  const { isAuthenticated, publicRoute, open, handleDrawerClose } = props;
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const theme = useTheme();
  const lgMediaDown = useMediaQuery(theme.breakpoints.down("lg"));
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const handleListItemClick = (_, index) => {
    setSelectedIndex(index);
  };

  ////// Drawer selected item change
  useEffect(() => {
    if (
      pathname === `${appURL}` ||
      pathname === `${appURL}/account` ||
      pathname === `${appURL}/company`
    ) {
      setSelectedIndex(0);
    }
    if (pathname.startsWith(`${appURL}/quotes`)) {
      setSelectedIndex(1);
    }
    if (pathname.startsWith(`${appURL}/invoices`)) {
      setSelectedIndex(2);
    }
    if (pathname.startsWith(`${appURL}/clients`)) {
      setSelectedIndex(3);
    }
    if (pathname.startsWith(`${appURL}/items-for-item-inv`)) {
      setSelectedIndex(5);
    }
    if (pathname.startsWith(`${appURL}/terms-of-payment`)) {
      setSelectedIndex(6);
    }
    if (pathname === `${appURL}/terms`) {
      setSelectedIndex(7);
    }
    if (pathname.startsWith(`${appURL}/countries`)) {
      setSelectedIndex(8);
    }
    if (pathname.startsWith(`${appURL}/currencies`)) {
      setSelectedIndex(9);
    }
    if (pathname.startsWith(`${appURL}/faq`)) {
      setSelectedIndex(10);
    }
    if (pathname.startsWith(`${appURL}/support`)) {
      setSelectedIndex(11);
    }
    if (pathname === `${appURL}/signup`) {
      setSelectedIndex(20);
    }
    if (pathname === `${appURL}/login`) {
      setSelectedIndex(21);
    }
  }, [pathname]);

  return (
    <>
      {publicRoute && lgMediaDown && (
        <List disablePadding sx={{ px: 1.25 }}>
          {[
            { index: 0, to: '/', label: t('general.main_page'), icon: <HomeIcon /> },
            { index: 1, to: '/#features', label: t('general.characteristics'), icon: <FlagIcon /> },
            { index: 2, to: '/#pricing', label: t('general.price'), icon: <PriceCheckIcon /> },
            { index: 3, to: `${appURL}/products`, label: t('general.products'), icon: <PaidIcon /> },
            { index: 4, to: `${appURL}/faq`, label: t('faq.frequent_questions'), icon: <QuizIcon /> },
            { index: 5, to: `${appURL}/support`, label: t('general.support'), icon: <SupportAgentIcon /> },
            ...(isAuthenticated
              ? [{ index: 20, to: appURL, label: t('dashboard.dashboard'), icon: <HomeIcon /> }]
              : [
                  { index: 20, to: `${appURL}/signup`, label: t('button.signup_for_free'), icon: <HowToRegIcon /> },
                  { index: 21, to: `${appURL}/login`, label: t('general.login'), icon: <LockOpenIcon /> },
                ]),
          ].map((item) => (
            <ListItemButtonStyled
              key={item.index}
              selected={selectedIndex === item.index}
              onClick={(event) => [handleListItemClick(event, item.index), handleDrawerClose()]}
              component={Link}
              to={item.to}
              sx={{ my: 0.5, py: 1.35, borderRadius: 2.5, '&:after': { display: 'none' } }}
            >
              <ListItemIconStyled>{item.icon}</ListItemIconStyled>
              <ListItemTextStyled primary={item.label} />
            </ListItemButtonStyled>
          ))}
        </List>
      )}

      {isAuthenticated && !publicRoute && pathname !== "/" && (
        <List disablePadding>
          <TooltipBasic
            title={t("dashboard.dashboard")}
            placement="right-start"
            disableHoverListener={open ? true : false}
          >
            <ListItemButtonStyled
              selected={selectedIndex === 0}
              component={Link}
              to={`${appURL}`}
              divider
              sx={{
                "&.MuiListItemButton-divider": {
                  borderColor: appChromePalette.border,
                },
              }}
              onClick={(event) => [
                handleListItemClick(event, 0),
                lgMediaDown && handleDrawerClose(),
              ]}
            >
              <ListItemIconStyled>
                <HomeIcon />
              </ListItemIconStyled>
              <ListItemTextStyled
                primary={t("dashboard.dashboard")}
                primaryTypographyProps={{
                  fontWeight: "800",
                }}
              />
            </ListItemButtonStyled>
          </TooltipBasic>

          <TooltipBasic
            title={t("general.quotes")}
            placement="right-start"
            disableHoverListener={open ? true : false}
          >
            <ListItemButtonStyled
              selected={selectedIndex === 1}
              onClick={(event) => [
                handleListItemClick(event, 1),
                lgMediaDown && handleDrawerClose(),
              ]}
              component={Link}
              to={`${appURL}/quotes`}
            >
              <ListItemIconStyled>
                <QuoteIcon />
              </ListItemIconStyled>
              <ListItemTextStyled primary={t("general.quotes_short")} />
            </ListItemButtonStyled>
          </TooltipBasic>

          <TooltipBasic
            title={t("general.invoices")}
            placement="right-start"
            disableHoverListener={open ? true : false}
          >
            <ListItemButtonStyled
              selected={selectedIndex === 2}
              component={Link}
              to={`${appURL}/invoices`}
              divider
              sx={{
                "&.MuiListItemButton-divider": {
                  borderColor: appChromePalette.border,
                },
              }}
              onClick={(event) => [
                handleListItemClick(event, 2),
                lgMediaDown && handleDrawerClose(),
              ]}
            >
              <ListItemIconStyled>
                <InvIcon />
              </ListItemIconStyled>
              <ListItemTextStyled primary={t("general.invoices")} />
            </ListItemButtonStyled>
          </TooltipBasic>

          <TooltipBasic
            title={t("general.clients")}
            placement="right-start"
            disableHoverListener={open ? true : false}
          >
            <ListItemButtonStyled
              selected={selectedIndex === 3}
              onClick={(event) => [
                handleListItemClick(event, 3),
                lgMediaDown && handleDrawerClose(),
              ]}
              component={Link}
              to={`${appURL}/clients`}
            >
              <ListItemIconStyled>
                <PeopleAltIcon />
              </ListItemIconStyled>
              <ListItemTextStyled primary={t("general.clients")} />
            </ListItemButtonStyled>
          </TooltipBasic>

          <TooltipBasic
            title={t("general.bank_accounts_drawer")}
            placement="right-start"
            disableHoverListener={open ? true : false}
          >
            <ListItemButtonStyled
              selected={selectedIndex === 4}
              onClick={(event) => [
                handleListItemClick(event, 4),
                lgMediaDown && handleDrawerClose(),
              ]}
              component={Link}
              to={`${appURL}/bank-accounts`}
            >
              <ListItemIconStyled>
                <EuroSymbolIcon />
              </ListItemIconStyled>
              <ListItemTextStyled primary={t("general.bank_accounts_drawer")} />
            </ListItemButtonStyled>
          </TooltipBasic>

          <TooltipBasic
            title={t("general.invoice_items")}
            placement="right-start"
            disableHoverListener={open ? true : false}
          >
            <ListItemButtonStyled
              selected={selectedIndex === 5}
              onClick={(event) => [
                handleListItemClick(event, 5),
                lgMediaDown && handleDrawerClose(),
              ]}
              component={Link}
              to="/apps/items-for-item-inv"
            >
              <ListItemIconStyled>
                <PriceCheckIcon />
              </ListItemIconStyled>
              <ListItemTextStyled primary={t("general.invoice_items")} />
            </ListItemButtonStyled>
          </TooltipBasic>

          <TooltipBasic
            title={t("general.terms_of_payment")}
            placement="right-start"
            disableHoverListener={open ? true : false}
          >
            <ListItemButtonStyled
              selected={selectedIndex === 6}
              onClick={(event) => [
                handleListItemClick(event, 6),
                lgMediaDown && handleDrawerClose(),
              ]}
              component={Link}
              to="/apps/terms-of-payment"
            >
              <ListItemIconStyled>
                <CreditCardIcon />
              </ListItemIconStyled>
              <ListItemTextStyled primary={t("general.terms_of_payment")} />
            </ListItemButtonStyled>
          </TooltipBasic>

          <TooltipBasic
            title={t("general.invoice_terms")}
            placement="right-start"
            disableHoverListener={open ? true : false}
          >
            <ListItemButtonStyled
              selected={selectedIndex === 7}
              component={Link}
              to="/apps/terms"
              divider
              sx={{
                "&.MuiListItemButton-divider": {
                  borderColor: appChromePalette.border,
                },
              }}
              onClick={(event) => [
                handleListItemClick(event, 7),
                lgMediaDown && handleDrawerClose(),
              ]}
            >
              <ListItemIconStyled>
                <GavelIcon />
              </ListItemIconStyled>
              <ListItemTextStyled primary={t("general.invoice_terms")} />
            </ListItemButtonStyled>
          </TooltipBasic>

          <TooltipBasic
            title={t("general.countries")}
            placement="right-start"
            disableHoverListener={open ? true : false}
          >
            <ListItemButtonStyled
              selected={selectedIndex === 8}
              onClick={(event) => [
                handleListItemClick(event, 8),
                lgMediaDown && handleDrawerClose(),
              ]}
              component={Link}
              to="/apps/countries"
            >
              <ListItemIconStyled>
                <FlagIcon />
              </ListItemIconStyled>
              <ListItemTextStyled primary={t("general.countries")} />
            </ListItemButtonStyled>
          </TooltipBasic>

          <TooltipBasic
            title={t("general.currencies")}
            placement="right-start"
            disableHoverListener={open ? true : false}
          >
            <ListItemButtonStyled
              selected={selectedIndex === 9}
              onClick={(event) => [
                handleListItemClick(event, 9),
                lgMediaDown && handleDrawerClose(),
              ]}
              component={Link}
              to="/apps/currencies"
            >
              <ListItemIconStyled>
                <PaidIcon />
              </ListItemIconStyled>
              <ListItemTextStyled primary={t("general.currencies")} />
            </ListItemButtonStyled>
          </TooltipBasic>

          {/*Questions and answers  */}
          <TooltipBasic
            title={t("faq.frequent_questions")}
            placement="right-start"
            disableHoverListener={open ? true : false}
          >
            <ListItemButtonStyled
              selected={selectedIndex === 10}
              onClick={(event) => [
                handleListItemClick(event, 10),
                lgMediaDown && handleDrawerClose(),
              ]}
              component={Link}
              to={`${appURL}/faq`}
              sx={{ height: "64px" }}
            >
              <ListItemIconStyled sx={{ alignSelf: "start", mt: 1 }}>
                <QuizIcon />
              </ListItemIconStyled>
              <ListItemTextStyled
                sx={{
                  display: "inline-block",
                  whiteSpace: "pre-line",
                }}
                primary={t("faq.frequent_questions")}
              />
            </ListItemButtonStyled>
          </TooltipBasic>

          {/*Support*/}
          <TooltipBasic
            title={t("general.support")}
            placement="right-start"
            disableHoverListener={open ? true : false}
          >
            <ListItemButtonStyled
              selected={selectedIndex === 11}
              onClick={(event) => [
                handleListItemClick(event, 11),
                lgMediaDown && handleDrawerClose(),
              ]}
              component={Link}
              to={`${appURL}/support`}
              sx={{ height: "64px" }}
            >
              <ListItemIconStyled sx={{ alignSelf: "start", mt: 1 }}>
                <SupportAgentIcon />
              </ListItemIconStyled>
              <ListItemTextStyled
                sx={{
                  display: "inline-block",
                  whiteSpace: "pre-line",
                }}
                primary={t("general.support")}
              />
            </ListItemButtonStyled>
          </TooltipBasic>
        </List>
      )}
    </>
  );
};

export default HeaderDrawerList;
